import {
    BlurEvent, ChangeEvent, ClickEvent, Component, CrudDecorator, DataHeader, DataSourceAction,
    DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DataSourceValidationEvent,
    DropdownSelectionEvent, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, PrintableEvent, Snackbar, Textbox
} from "@mcleod/components";
import { AutogenLayoutOfficePayee } from "./autogen/AutogenLayoutOfficePayee";
import {
    Api, Currency, CurrencySettings, CurrencyUtil, DateUtil, HorizontalAlignment, Model, ModelRow, Navigation,
    NumberUtil, PermissionsUtil, UrlUtil
} from "@mcleod/core";
import { AllocType } from "../SegAllocDetailTable";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
import { CommonDialogs, ImageTable } from "@mcleod/common";
import { AutogenLayoutJobClassLookup } from "../autogen/AutogenLayoutJobClassLookup";
import { LeaveHistory } from "../LeaveHistory";
import { getPayrollControlBoolean } from "@mcleod/general";
import { Office401kRates } from "./Office401kRates";
import { DrsPayee } from "../DrsPayee";
import { getSegAllocControlBoolean } from "../models/ModelSegAllocControl";

const viewOffPayeeUrl = "lme/settlement/officepayroll/OfficePayee?mode=update&key=";
const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "payee",
    screenName: "Office Payee",
    navigateToUrl: viewOffPayeeUrl
};
const DIRECT_DEPOSIT = "D";
const TAX_INCOME = "tax_table_income";
const TAX_UNEMPLOYMENT = "tax_table_unemployment";
const ACCRUES_BEGIN_YEAR = "B";
const ACCRUES_ANNIVERSARY = "A";


export class OfficePayee extends AutogenLayoutOfficePayee {

    codeGenerationIsActive: boolean;
    allowManualEntry: boolean;
    private isMultiCurrencyEnabled: boolean;
    private voidCommissions: boolean = false;
    private hasSettlement401kRecord: boolean;
    private setSettlementEmployee: boolean;
    private drsPayeeLayout: DrsPayee;
    private officeCheckboxOriginalValue: boolean;
    private showSaveAndCloseOnDataHeader: boolean;

    private currencyTextBoxes: Textbox[];

    override async onLoad() {
        this.isMultiCurrencyEnabled = CurrencySettings.getSingleton()?.multiCurrencyEnabled();
        this.tabCommissions.visible = getPayrollControlBoolean("enable_commissions", false);

        this.layoutSegAllocDetail.allocType = AllocType.OFFICEPAYEE;

        this.layoutDirectDeposit.useMethodSetup();
        this.layoutDirectDeposit.tableDirectDeposit.addContentsChangedListener(() => {
            this.setDirectDepositPercentageCount();
        });
        this.layoutDirectDeposit.tableDirectDeposit.addRowCreateListener(() => {
            this.setDirectDepositPercentageCount();
        });

        this.layoutTableLeaveSick.mainDataSource.orderBy = [
            { field: "check_date", sort: "desc" },
            { field: "payee_id", sort: "asc" },
            { field: "applies_to", sort: "asc" },
            { field: "id", sort: "desc" }
        ];
        this.layoutTableLeaveVacation.mainDataSource.orderBy = [
            { field: "check_date", sort: "desc" },
            { field: "payee_id", sort: "asc" },
            { field: "applies_to", sort: "asc" },
            { field: "id", sort: "desc" }
        ];
        this.currencyTextBoxes = [
            this.textboxDependents,
            this.textboxOtherIncome,
            this.textboxFedAmtToWhold,
            this.textboxAddFedToWh,
            this.textboxOtherDeductions,
            this.textboxExtraWithholdings,
            this.textboxStAmtToWhold,
            this.textboxAddlStToWhold,
            this.textboxCountyAmtToWhold,
            this.textboxCityAmtToWhold
        ];

        this.addLayoutLoadListener(() => {
            this.currencyTextBoxes.forEach(textbox => {
                textbox.addBlurListener((event: BlurEvent) => {
                    const textbox = event.target as Textbox;
                    this.updateCurrencyOnBlur(textbox.field);
                });
            });
        });

        this.mainDataSource.addValidationListener((event: DataSourceValidationEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
                return;
            }
            this.addJobClassificationValidation(event);
            if (!this.activeRow.getBoolean("office_employee", false) && !this.activeRow.getBoolean("non_office_emp", false)) {
                event.validationResults.push({
                    isValid: false,
                    component: this.checkboxOffice,
                    validationMessage: "Payee record must be marked for Office and/or Settlement.",
                    caption: "Payee record must be marked for Office and/or Settlement."
                });
            }
        });

        this.mainDataSource.doBeforeDataSourcePost = async () => {
            if (!(await DuplicateId.validateBeforePost(this.textboxCode, DUPLICATE_VALIDATION_PROPS))) {
                return false;
            }

            if (this.isMultiCurrencyEnabled) {
                if (this.mainDataSource.mode != DataSourceMode.ADD && this.activeRow.get("currency") != this.activeRow.originalData?.["currency"]) {
                    const result = await CommonDialogs.showYesNo(
                        "It is strongly recommended that you consult with your McLeod Software account manager " +
                        "before making changes to the Currency of the Payee.\n\n" +
                        "Changing the Transaction Currency of the Payee will cause unintended results in all " +
                        "money fields for this payee throughout the system. The existing values already stored " +
                        "will not be re-calculated causing out of balance issues for screens, reports, journals, etc.\n\n" +
                        "Proceed with update of this field?"
                    );

                    if (!result) {
                        return false;
                    }
                }
            }

            if (DataSourceMode.SEARCH !== this.mainDataSource.mode && this.activeRow.get("payment_method") == "D") {
                if (!this.layoutDirectDeposit.validateDirectDepositPercentageDistribution(this.textboxPayeeachBatchClassCode, this)) {
                    return false;
                }
            }

            for (const row of this.layout401kRates.tablePayee401kRate.rows) {
                const rateDetailsLayout = this.layout401kRates.rateDetailsMap.get(row.data.get("unique_id"));
                if (rateDetailsLayout) {
                    rateDetailsLayout.table401kDetails.dataSource.parentDataSource = this.mainDataSource;
                }
            }

            this.activeRow.set("void_commissions", this.voidCommissions);

            if (!this.mainDataSource.validate()) {
                return false;
            }
            if (DataSourceMode.SEARCH !== this.mainDataSource.mode && this.activeRow.getChangedData()["non_office_emp"] === "Y") {
                this.drsPayeeLayout = Layout.getLayout("lme/settlement/DrsPayee") as DrsPayee;
                await this.drsPayeeLayout.ensureLoaded();
                if (this.mainDataSource.mode === DataSourceMode.UPDATE) {
                    await this.drsPayeeLayout.mainDataSource.search({
                        id: this.activeRow.get("id"),
                        ignore_settlement_flag: true
                    }, null, this.drsPayeeLayout.layoutName);
                    if (this.drsPayeeLayout.activeRow.isNull("payee_type")) {
                        // get most recent payee type by querying payment history for payee then set on activeRow
                        try {
                            const payeeId = this.activeRow.get("id");
                            if (payeeId) {
                                const response = await Api.search("lme/settlement/DrsPayrollHist", {
                                    payee_id: payeeId,
                                    _max_search_results: 1
                                });

                                // Api.search responses commonly expose a plain data array
                                const firstDataRow = response?.data?.[0];
                                let payeeType: string = firstDataRow?.payee_type;

                                // Fallback in case response returns modelRows
                                if (!payeeType && Array.isArray(response?.modelRows) && response.modelRows.length > 0) {
                                    const modelRow = response.modelRows[0];
                                    payeeType = modelRow?.get("payee_type");
                                }

                                if (payeeType) {
                                    this.drsPayeeLayout.textboxTypeOf.setSelectedItemFromValue(payeeType); // trigger afterDropdownSelection event
                                    this.drsPayeeLayout.activeRow.set("drs_payee.type_of", payeeType); // add to changed data
                                }
                            }
                        } catch (e) {
                            // If we cannot determine a payee type from history, continue without blocking save
                        }
                    }
                }
                if (this.drsPayeeLayout.validate().filter(result => !result.isValid).length > 0) {
                    const isYes = await CommonDialogs.showYesNo("To save this payee as a settlement payee, required fields on the Settlement Payee screen must be completed. Would you like to update them now?", "Settlement Payee");
                    if (isYes) {
                        this.setSettlementEmployee = true;
                    }
                    this.activeRow.set("non_office_emp", "N");
                    this.officeCheckboxOriginalValue = this.activeRow.getBoolean("office_employee");
                    this.activeRow.set("office_employee", "Y");
                }
                else if (this.drsPayeeLayout.mainDataSource.hasChanged(true)) {
                    await this.drsPayeeLayout.mainDataSource.post();
                }
            }

            return true;
        };
        this.layoutTableComment.displayDispatchField = true;
        this.layoutTableComment.displayCopyOrderFields = false;
        Office401kRates.setTableParentDataSource(this.layout401kRates.tablePayee401kRate, this.mainDataSource);
        this.layout401kRates.parentDataSource = this.mainDataSource;
        this.layout401kRates.textboxId.visible = false;
        this.layout401kRates.textboxId.required = false;
        this.checkboxSettlement.addChangeListener((event: ChangeEvent) => {
            if (event.userInitiatedChange) {
                if (event.newValue === true) {
                    this.dataHeader.showSaveAndClose = false;
                } else {
                    this.dataHeader.showSaveAndClose = this.showSaveAndCloseOnDataHeader;
                }
            }
        });
    }

    doAfterDataHeaderSetup(dataHeader: DataHeader) {
        this.showSaveAndCloseOnDataHeader = dataHeader.showSaveAndClose;
        super.doAfterDataHeaderSetup(dataHeader);
    }

    addJobClassificationValidation(event: DataSourceValidationEvent) {
        if (this.activeRow.isNull("off_payee.division_id")) {
            event.validationResults.push({
                isValid: false,
                component: this.textboxOffPayeedivisionId,
                validationMessage: this.textboxOffPayeedivisionId.caption
            });
        }
        if (this.activeRow.isNull("off_payee.terminal_id")) {
            event.validationResults.push({
                isValid: false,
                component: this.textboxOffPayeeterminalId,
                validationMessage: this.textboxOffPayeeterminalId.caption
            });
        }
        if (this.activeRow.isNull("off_payee.job_class_code_id")) {
            event.validationResults.push({
                isValid: false,
                component: this.textboxOffPayeejobClassCodeId,
                validationMessage: this.textboxOffPayeejobClassCodeId.caption
            });
        }
    }

    setupComponentListeners() {
        this.textboxPaymentMethod.addChangeListener((event: ChangeEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
                return;
            }
            if (this.activeRow == null) {
                return;
            }
            this.textboxPaymentMethod.setSelectedItemFromDisplayValue(event.newValue);
            const dbValue = this.textboxPaymentMethod.selectedItem?.value;

            this.setupDirectDepositTab(dbValue == DIRECT_DEPOSIT);

            if (dbValue != DIRECT_DEPOSIT) {
                this.activeRow.set("ach_batch_class_code", null);
            }

            // Set Contract Fields
            let showContractTransferFields = false;

            if (this.activeRow.get("is_contract_transfer") == true && dbValue == "T") {
                showContractTransferFields = true;
            }

            this.panelPayToFour.visible = showContractTransferFields;

            this.textboxParentContract.visible = showContractTransferFields;
            this.textboxEfsAccount.visible = showContractTransferFields;
            this.textboxChildContract.visible = showContractTransferFields;

            this.textboxParentContract.required = showContractTransferFields;
            this.textboxEfsAccount.required = showContractTransferFields;
            this.textboxChildContract.required = showContractTransferFields;
        });
        this.switchEFTForIndividual.addChangeListener((event: ChangeEvent) => {
            this.setEFTFieldsVisibility();
        });
        this.textboxCode.addChangeListener((event: ChangeEvent) => {
            if (event.userInitiatedChange && this.mainDataSource.mode != DataSourceMode.SEARCH) {
                this.sourceOffRecurDeduct.data.forEach(row => {
                    row.set("payee_id", event.newValue);
                });
            }
        });
    }

    setEFTFieldsVisibility() {
        this.textboxEftFirstName.visible = this.textboxEftMiddleInitial.visible = this.textboxEftLastName.visible = this.switchEFTForIndividual.checked;
        this.textboxEftFirstName.required = this.textboxEftLastName.required = this.switchEFTForIndividual.checked;
    }


    async sourceOffPayeeAfterModeChange(event: DataSourceModeChangeEvent) {
        if (this.activeRow == null) {
            return;
        }
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            this.setupDirectDepositTab(true);
        }
        this.setAllocationVisible();
        this.setTitle();
        this.setEFTFieldsVisibility();
        this.setupLeaveTab();
        if (event.newMode === DataSourceMode.ADD || event.newMode === DataSourceMode.UPDATE) {
            this.setupComponentListeners();
            this.setup401kRatesTab();

            if (event.newMode === DataSourceMode.ADD) {
                this.textboxStatus.setSelectedItemFromValue("A");
                if (this.textboxCurrency.visible) {
                    this.activeRow.setValueAndLookupModelData("currency", CurrencySettings.getTransactionalCurrency(), { currency_type_id: CurrencySettings.getTransactionalCurrency() });
                }
                this.textboxStatus.removeItemsWithValue("N");
            }

            if (PermissionsUtil.isUserDeniedLayout("lme/settlement/DrsPayee")) {
                if (event.newMode === DataSourceMode.ADD) {
                    this.checkboxOffice.visible = false;
                    this.checkboxSettlement.visible = false;
                }
                else if (event.newMode === DataSourceMode.UPDATE) {
                    this.checkboxSettlement.enabled = false;
                }
            }
        }
        this.layoutRecurringPayAdjustment.payeeDataSource = this.mainDataSource;
    }

    sourceOffPayeeAfterExecution(event: DataSourceExecutionEvent) {
        if (this.activeRow == null) {
            return;
        }
        (this.layoutImageTable as ImageTable).populateForRow("P", this.mainDataSource.activeRow.get("id"));

        if (event.getAction() === DataSourceAction.SEARCH) {
            this.checkIfPayeeIsBeingProcessed();
            this.setupDirectDepositTab(this.activeRow.get("payment_method") == DIRECT_DEPOSIT);
            this.setupTaxTab();
            this.setup401kRatesTab();
            this.setupGeneralTab();
            this.setupLeaveTab();
            this.setupSettlementPayeeButton();
        }
        if (event.getAction() === DataSourceAction.UPDATE) {
            this.setupSettlementPayeeButton();
        }
        if (this.activeRow.get("status") !== "N") {
            this.textboxStatus.removeItemsWithValue("N");
        }
        if (this.setSettlementEmployee) {
            this.openSettlementPayeeScreen();
        }
        this.dataHeader.showSaveAndClose = this.showSaveAndCloseOnDataHeader;
    }

    async openSettlementPayeeScreen() {
        this.setSettlementEmployee = false;
        const urlKey = this.activeRow.get("id");
        if (this.drsPayeeLayout.activeRow == null) {
            await this.drsPayeeLayout.mainDataSource.search({
                id: this.activeRow.get("id"),
                ignore_settlement_flag: true
            }, null, this.drsPayeeLayout.layoutName);
        }
        const urlParams = { mode: DataSourceMode.UPDATE, key: urlKey };
        const crud = new CrudDecorator({
            pseudoNavigateUrl: "lme/settlement/DrsPayee" + UrlUtil.buildQueryString(urlParams),
            layout: this.drsPayeeLayout,
            headerProps: {
                showClose: true,
                showSaveAndClose: true
            },
            doAfterSlideIn: () => {
                this.drsPayeeLayout.checkboxNonOfficeEmp.checked = true;
                this.drsPayeeLayout.checkboxOfficeEmployee.checked = this.officeCheckboxOriginalValue;
                this.drsPayeeLayout.checkboxNonOfficeEmp.enabled = false;
                this.drsPayeeLayout.checkboxOfficeEmployee.enabled = false;
                this.drsPayeeLayout.mainDataSource.post();
            },
            doAfterSlideOut: () => {
                this.search({ id: this.activeRow.get("id") });
            },
            mode: DataSourceMode.UPDATE
        });
        crud.slideIn({ speed: 200 });
    }

    async checkIfPayeeIsBeingProcessed() {
        const hasProcessStatus = this.activeRow.get("off_payee.process_status") != null;
        if (hasProcessStatus) {
            Snackbar.showWarningSnackbar("Payee is in office payroll processing. Any changes to the payee file may affect payroll totals.", { persist: true });
            this.textboxPaymentMethod.enabled = false;
            this.switchFreezePayables.enabled = false;
            return true;
        }
    }

    private setTitle() {
        if (!this.activeRow.isNull("name")) {
            this.titleEditModeSuffix = "{name}";
        } else {
            this.titleEditModeSuffix = "{check_name}";
        }
    }

    textboxEarningCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.code_type = "E";
    }

    textboxPayOptionAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.userInitiatedChange && !event.hasSelection()) {
            this.activeRow.set("method", null);
            this.activeRow.set("detention_pay_rate", null);
            this.activeRow.set("detention_max_pay_amt", null);
            this.activeRow.set("earning_code", null);
        }
    }

    private setAllocationVisible() {
        const isAllocationEnabled = getSegAllocControlBoolean("is_active", false);
        this.tabAccounting.visible = isAllocationEnabled || CurrencySettings.getSingleton().multiCurrencyEnabled();
        this.layoutSegAllocDetail.visible = isAllocationEnabled;
    }

    private setDirectDepositPercentageCount() {
        const [hasPercentageRow, percentageTotals] = this.layoutDirectDeposit.getDirectDepositPercentageTotal();

        if (hasPercentageRow) {
            this.labelDirectDepositStatusMessage.visible = true;
            this.labelDirectDepositStatusMessage.fontBold = true;
            this.labelDirectDepositStatusMessage.fontSize = "xxlarge";
            this.labelDirectDepositStatusMessage.color = "default";
            this.labelDirectDepositStatusMessage.text = this.isMultiCurrencyEnabled ? "Percentage total(s): " : "Percentage total: ";
            for (const [currencyType, percentage] of percentageTotals.entries()) {
                this.labelDirectDepositStatusMessage.text += this.isMultiCurrencyEnabled ? `${currencyType} ${percentage}% ` : `${percentage}% `;
            }

            if (!this.layoutDirectDeposit.percentagesValid(percentageTotals)) {
                this.labelDirectDepositStatusMessage.color = "warning";
            }
        } else {
            this.labelDirectDepositStatusMessage.visible = false;
        }
    }



    textboxFuelSurchargePayBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.surcharge_type = "R";
    }

    textboxQuickpayBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.code_type = "E";
    }

    switchPayeeemailSummaryOnChange(event: ChangeEvent) {
        this.textboxPayeeemail.required = this.switchPayeeemailSummary.checked;
    }

    textboxPayeenameOnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode == DataSourceMode.ADD) {
            if (this.activeRow.isNull("check_name")) {
                this.activeRow.set("check_name", this.activeRow.get("name"));
            }
            if (this.activeRow.isNull("legal_name")) {
                this.activeRow.set("legal_name", this.activeRow.get("name"));
            }
        }
    }

    textboxPayeeaddress1OnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode == DataSourceMode.ADD) {
            if (this.activeRow.isNull("check_address")) {
                this.activeRow.set("check_address", this.activeRow.get("address1"));
            }
        }
    }

    textboxPayeeaddress2OnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode == DataSourceMode.ADD) {
            if (this.activeRow.isNull("check_address2")) {
                this.activeRow.set("check_address2", this.activeRow.get("address2"));
            }
        }
    }

    citystatePayeeCityIdOnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode == DataSourceMode.ADD) {
            if (this.activeRow.isNull("check_city")) {
                this.activeRow.set("check_city", this.activeRow.get("city"));
            }
            if (this.activeRow.isNull("check_st")) {
                this.activeRow.set("check_st", this.activeRow.get("state"));
            }
            if (this.activeRow.isNull("check_zip")) {
                this.activeRow.set("check_zip", this.activeRow.get("zip_code"));
            }
            if (this.activeRow.isNull("check_city_id")) {
                this.activeRow.set("check_city_id", this.activeRow.get("city_id"));
            }
        }
    }

    sourceOffPayeeBeforeModeChange(event) {
        if (event.newMode === DataSourceMode.ADD) {
            this.textboxCode.enabledDuringAdd = true;
            Api.post("lme/general/code-generation-settings", { "table_name": "payee" }).then(response => {
                this.codeGenerationIsActive = response.data[0]?.is_active ?? false;
                this.allowManualEntry = response.data[0]?.allow_manual_entry ?? false;
                this.textboxCode.required = !this.codeGenerationIsActive;
                if (this.codeGenerationIsActive) {
                    this.textboxCode.caption = "*Payee Code";
                    this.textboxCode.enabledDuringAdd = this.allowManualEntry;
                }

                DUPLICATE_VALIDATION_PROPS.codeGenerationIsActive = this.codeGenerationIsActive;
                DuplicateId.configureIdTextbox(this.textboxCode, DUPLICATE_VALIDATION_PROPS);
            });
        }
    }

    setupDirectDepositTab(isDirectDeposit: boolean) {
        this.textboxPayeeachBatchClassCode.visible = isDirectDeposit;
        this.layoutDirectDeposit.visible = isDirectDeposit;
        this.tabDirectDeposit.visible = isDirectDeposit;
    }

    setupGeneralTab() {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH) {
            this.textboxLastRaiseDate.visible = true;
            this.buttonClearClassification.enabled =
                !this.activeRow.isNull("off_payee.division_id") ||
                !this.activeRow.isNull("off_payee.terminal_id") ||
                !this.activeRow.isNull("off_payee.job_class_code_id");
        }
    }

    setupLeaveTab() {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            this.buttonAdjustLeave.visible = false;
            return;
        }


        if (this.mainDataSource.mode === DataSourceMode.ADD) {
            this.buttonAdjustLeave.visible = false;
            this.layoutTableLeaveSick.enabled = false;
            this.layoutTableLeaveVacation.enabled = false;
        }

        if (this.mainDataSource.mode === DataSourceMode.UPDATE) {
            this.buttonAdjustLeave.visible = true;
            const whenVacAccrues = this.activeRow.get("off_payee.when_vac_accrues");
            const whenSickAccrues = this.activeRow.get("off_payee.when_sick_accrues");
            this.textboxOffPayeevacationHrsBase.enabled = whenVacAccrues !== ACCRUES_BEGIN_YEAR && whenVacAccrues !== ACCRUES_ANNIVERSARY;
            this.textboxOffPayeesickHoursBase.enabled = whenSickAccrues !== ACCRUES_BEGIN_YEAR && whenSickAccrues !== ACCRUES_ANNIVERSARY;
            this.layoutTableLeaveSick.showSlideout = LeaveHistory.showOfficeAsSlideout;
        }
    }

    async setup401kRatesTab() {
        this.tab401kRates.visible = true;
        this.layout401kRates.mainDataSource.removeValidationListener(this.layout401kRates.validationListener);
        this.layout401kRates.textboxId.required = false;
        if (this.tab401kRates.visible) {
            if (this.hasSettlement401kRecord == null && this.mainDataSource.mode === DataSourceMode.UPDATE) {
                this.hasSettlement401kRecord = await Model.searchSingleRecord("lme/settlement/payee-401krate-hdr", {
                    category: "D",
                    id: this.activeRow.get("id")
                }) != null;
            }
            this.tab401kRates.visible = !this.hasSettlement401kRecord;
        }
    }

    textBoxRightAlignPrintable(event: PrintableEvent, width: number) {
        if (event.printable) {
            const textbox = event.target as Textbox;
            textbox.width = width;
            textbox.align = HorizontalAlignment.RIGHT;
            textbox.captionProps = { align: HorizontalAlignment.RIGHT };
        }
    }

    textBoxLeftAlignPrintable(event: PrintableEvent, width: number) {
        if (event.printable) {
            const textbox = event.target as Textbox;
            event.printableLabel.align = HorizontalAlignment.LEFT;
            textbox.captionProps = { align: HorizontalAlignment.LEFT };
        }
    }

    textBoxLeftAlignOnPrintable(event: PrintableEvent) {
        this.textBoxLeftAlignPrintable(event, 160);
    }

    textBoxRightAlignOnPrintable(event: PrintableEvent) {
        this.textBoxRightAlignPrintable(event, 160);
    }

    textBoxRightAlignSmallOnPrintable(event: PrintableEvent) {
        this.textBoxRightAlignPrintable(event, 80);
    }

    /**
     * Configures tax-related fields based on payee type, W4 status, and tax table settings
     * Fields are shown/hidden appropriately for different tax scenarios
     */
    async setupTaxTab() {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            return; // No UI adjustments needed in search mode
        }

        this.textboxStMaritalStatus.required = !this.activeRow.isNull("state_tax_code");


        this.tabTaxes.visible = true;
        this.textboxFedMaritalStatus.required = true;

        // Extract table configuration flags
        const flags = {
            useFedTaxTable: this.activeRow?.getBoolean("fed_auto_tax_table") || false,
            useStateTaxTable: this.activeRow?.getBoolean("state_auto_tax_table") || false,
            useSuiStateTaxTable: this.activeRow?.getBoolean("sui_auto_tax_table") || false,
            useCountyTaxTable: this.activeRow?.getBoolean("county_auto_tax_table") || false,
            useCityTaxTable: this.activeRow?.getBoolean("city_auto_tax_table") || false
        };

        // Determine W4 version
        const isPrior2020W4 = this.activeRow.get("w4_status") === "P";

        // Define field visibility requirements by context using direct component references
        const fieldGroups: Record<string, Map<Component, boolean>> = {
            // Pre-2020 W4 fields
            prior2020Fields: new Map<Component, boolean>([
                [this.textboxFedExemptions, true],
                [this.textboxAddFedToWh, true],
                [this.textboxFedAmtToWhold, true],
                [this.textboxTaxTableFitMisc, flags.useFedTaxTable],
                [this.textboxTaxTableFitAux, flags.useFedTaxTable]
            ]),

            // Current W4 fields (2020 and later)
            currentW4Fields: new Map<Component, boolean>([
                [this.textboxDependents, true],
                [this.textboxOtherIncome, true],
                [this.textboxOtherDeductions, true],
                [this.textboxExtraWithholdings, true],
                [this.checkboxExemptFromFed, true],
                [this.checkboxMultipleJobs, true]
            ]),


            // State tax table fields
            stateTaxFields: new Map<Component, boolean>([
                [this.textboxTaxTableStAux, flags.useStateTaxTable],
                [this.textboxTaxTableStMisc, flags.useStateTaxTable]
            ]),

            // SUI tax table fields
            suiTaxFields: new Map<Component, boolean>([
                [this.textboxTaxTableSuiAux, flags.useSuiStateTaxTable],
                [this.textboxTaxTableSuiMisc, flags.useSuiStateTaxTable]
            ]),

            // County tax table fields
            countyTaxFields: new Map<Component, boolean>([
                [this.textboxTaxTableCountyAux, flags.useCountyTaxTable],
                [this.textboxTaxTableCountyMisc, flags.useCountyTaxTable],
                [this.textboxCountyExemptions, flags.useCountyTaxTable],
                [this.textboxCountyAmtToWhold, flags.useCountyTaxTable]
            ]),

            // City tax table fields
            cityTaxFields: new Map<Component, boolean>([
                [this.textboxTaxTableCityAux, flags.useCityTaxTable],
                [this.textboxTaxTableCityMisc, flags.useCityTaxTable],
                [this.textboxCityExemptions, flags.useCityTaxTable],
                [this.textboxCityAmtToWhold, flags.useCityTaxTable]
            ])
        };

        // Hide all fields first
        this.hideTaxFields(fieldGroups);

        // Apply federal tax field visibility based on W4 version
        const relevantFields = isPrior2020W4 ? fieldGroups.prior2020Fields : fieldGroups.currentW4Fields;
        this.showFieldsMatchingCondition(relevantFields);

        // Show all tax fields that are enabled by their respective flags
        [
            fieldGroups.stateTaxFields,
            fieldGroups.suiTaxFields,
            fieldGroups.countyTaxFields,
            fieldGroups.cityTaxFields
        ].forEach(fields => this.showFieldsMatchingCondition(fields));

        // Add tooltips for state, SUI, county, and city tax fields if instructions are available
        const stateInstructions = this.activeRow.get("state_instructions");
        const suiInstructions = this.activeRow.get("sui_instructions");
        const countyInstructions = this.activeRow.get("county_instructions");
        const cityInstructions = this.activeRow.get("city_instructions");

        if (flags.useStateTaxTable && stateInstructions) {
            this.textboxTaxTableStAux.tooltip = stateInstructions;
            this.textboxTaxTableStMisc.tooltip = stateInstructions;
        }

        if (flags.useSuiStateTaxTable && suiInstructions) {
            this.textboxTaxTableSuiAux.tooltip = suiInstructions;
            this.textboxTaxTableSuiMisc.tooltip = suiInstructions;
        }

        if (flags.useCountyTaxTable && countyInstructions) {
            this.textboxTaxTableCountyAux.tooltip = countyInstructions;
            this.textboxTaxTableCountyMisc.tooltip = countyInstructions;
            this.textboxCountyExemptions.tooltip = countyInstructions;
        }

        if (flags.useCityTaxTable && cityInstructions) {
            this.textboxTaxTableCityAux.tooltip = cityInstructions;
            this.textboxTaxTableCityMisc.tooltip = cityInstructions;
            this.textboxCityExemptions.tooltip = cityInstructions;
        }

    }

    private showFieldsMatchingCondition(fields: Map<Component, boolean>) {
        fields.forEach((shouldShow, field) => {
            if (shouldShow && field) {
                field.visible = true;
            }
        });
    }

    private hideTaxFields(fieldGroups: Record<string, Map<Component, boolean>>) {
        // Process all field groups
        Object.values(fieldGroups).forEach(fieldGroup => {
            fieldGroup.forEach((_, field) => {
                if (field) {
                    field.visible = false;
                }
            });
        });
    }

    async getStatTaxTable(stateCode: string, taxTableType: string): Promise<ModelRow> {
        return await Model.searchSingleRecord("lme/ar/statetax", {
            id: stateCode,
            include_instructions: true,
            tax_table_type: taxTableType
        }, ["use_auto_tax_table", "instructions"]);
    }


    /** This is an event handler for the onChange event of switchW4Status. */
    switchW4StatusOnChange(event: ChangeEvent) {
        if (this.activeRow) {
            this.setupTaxTab();
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxStateTaxCode. */
    textboxStateTaxCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.type_of = "S";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxSuiTaxCode. */
    textboxSuiTaxCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.type_of = "S";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxCountyTaxCode. */
    textboxCountyTaxCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.type_of = "O";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxCityTaxCode. */
    textboxCityTaxCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.type_of = "C";
    }


    /** This is an event handler for the onLookupModelSelection event of textboxStateTaxCode. */
    async textboxStateTaxCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const boxes = [this.textboxTaxTableStAux, this.textboxTaxTableStMisc];
        if (!event.hasSelection()) {
            this.hideTextBoxes(boxes);
        }
        this.handleTaxCodeSelection(
            event,
            TAX_INCOME,
            boxes
        );
        this.textboxStMaritalStatus.required = event.hasSelection();
    }

    /** This is an event handler for the onLookupModelSelection event of textboxSuiTaxCode. */
    async textboxSuiTaxCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const boxes = [this.textboxTaxTableSuiMisc, this.textboxTaxTableSuiAux];
        if (!event.hasSelection()) {
            this.hideTextBoxes(boxes);
        }
        await this.handleTaxCodeSelection(
            event,
            TAX_UNEMPLOYMENT,
            boxes
        );
        this.setFocustoFirstVisibleField(boxes);
    }

    /** This is an event handler for the onLookupModelSelection event of textboxCountyTaxCode. */
    async textboxCountyTaxCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const boxes = [this.textboxCountyExemptions, this.textboxTaxTableCountyMisc, this.textboxTaxTableCountyAux, this.textboxCountyAmtToWhold];
        if (!event.hasSelection()) {
            this.hideTextBoxes(boxes);
        }
        await this.handleTaxCodeSelection(
            event,
            TAX_INCOME,
            boxes
        );
        this.setFocustoFirstVisibleField(boxes);
    }

    /** This is an event handler for the onLookupModelSelection event of textboxCityTaxCode. */
    async textboxCityTaxCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const boxes = [this.textboxCityExemptions, this.textboxTaxTableCityMisc, this.textboxTaxTableCityAux, this.textboxCityAmtToWhold];
        if (!event.hasSelection()) {
            this.hideTextBoxes(boxes);
        }
        await this.handleTaxCodeSelection(
            event,
            TAX_INCOME,
            boxes
        );
        this.setFocustoFirstVisibleField(boxes);
    }

    private setFocustoFirstVisibleField(boxes: Textbox[]) {
        // Focus on the first visible textbox
        for (const box of boxes) {
            if (box.visible) {
                box.focus();
                break; // Stop after focusing the first visible box
            }
        }
    }

    private async handleTaxCodeSelection(
        event: LookupModelSelectionEvent,
        taxTableType: string,
        fieldsToUpdate: Component[]) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            return; // No need to modify fields in search mode
        }

        const stateCode = event.selection?.get("id");
        if (stateCode) {
            const taxTable = await this.getStatTaxTable(stateCode, taxTableType);
            const useTaxTable = taxTable?.getBoolean("use_auto_tax_table") || false;

            // Update visibility for all provided fields
            fieldsToUpdate.forEach(field => {
                if (field) {
                    field.visible = useTaxTable;
                    if (useTaxTable) {
                        field.tooltip = !taxTable.isNull("instructions") ? taxTable.get("instructions") : null;
                    }
                }
            });
        }
    }

    private hideTextBoxes(textboxes: Textbox[]) {
        textboxes.forEach(textbox => {
            textbox.visible = false;
        });
    }

    /**
     * Updates currency fields when currency selection changes
     */
    textboxCurrencyOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (!this.isMultiCurrencyEnabled) return;

        if (event.hasSelection()) {
            const targetCurrencyCode = event.selection.get("currency_type_id");

            if (this.mainDataSource.mode === DataSourceMode.ADD) {
                // For ADD mode, update currency codes without conversion
                this.updateCurrencyFieldWithNewCode(this.getFieldsToConvert(), targetCurrencyCode);
            } else if (this.mainDataSource.mode === DataSourceMode.UPDATE) {
                // For UPDATE mode, convert existing values to the new currency
                this.convertCurrencyField(this.getFieldsToConvert(), targetCurrencyCode);
            }
        }
    }

    private getFieldsToConvert(): string[] {
        return this.currencyTextBoxes.map(textbox => textbox.field);
    }

    /**
     * Updates a Currency field with a new currency code without changing the amount
     */
    private updateCurrencyFieldWithNewCode(fields: string[], currencyCode: string): void {
        const values: Currency[] = fields.map(field => this.activeRow.getCurrency(field)).filter(value => value != null);
        if (values.length === 0) return;
        fields.forEach(field => {
            const currentValue = this.activeRow.getCurrency(field);
            if (currentValue) {
                const updatedValue = new Currency({
                    amount: currentValue.getAmount(),
                    currency_code: currencyCode
                });
                this.activeRow.set(field, updatedValue);
            }
        });
    }

    /**
     * Converts a Currency field value to the specified currency code
     */
    private convertCurrencyField(fields: string[], targetCurrencyCode: string): void {
        const currencyMap = new Map<string, Currency>();
        fields.forEach(field => {
            const currencyValue = this.activeRow.getCurrency(field);
            if (currencyValue != null) {
                currencyMap.set(field, currencyValue);
            }
        });

        const values: Currency[] = Array.from(currencyMap.values());
        if (values.length === 0) return;
        CurrencyUtil.convertCurrencies(
            values,
            targetCurrencyCode
        ).then(convertedCurrencies => {
            let index = 0;
            currencyMap.forEach((_, field) => {
                this.activeRow.set(field, convertedCurrencies[index]);
                index++;
            });
        });
    }

    /**
     * Common handler for updating currency fields on blur events
     */
    private updateCurrencyOnBlur(fieldPath: string): void {
        if (!this.isMultiCurrencyEnabled ||
            (this.mainDataSource.mode !== DataSourceMode.ADD && this.mainDataSource.mode !== DataSourceMode.UPDATE) ||
            this.activeRow.isNull("currency")) {
            return;
        }

        this.updateCurrencyFieldWithNewCode([fieldPath], this.activeRow.get("currency"));
    }


    /** This is an event handler for the onChange event of checkboxDisablePayrollTaxes. */
    checkboxDisablePayrollTaxesOnChange(event: ChangeEvent) {
        this.textboxFedMaritalStatus.required = !event.newValue;
    }

    /** This is an event handler for the onClick event of buttonJobClassification. */
    async buttonJobClassificationOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/settlement/JobClassLookup") as AutogenLayoutJobClassLookup;
        CommonDialogs.showDialog(layout, {
            title: "Job Classification Lookup",
            okProps: {
                onClick: () => {
                    if (layout.tableJobClass.selectedRow != null) {
                        const selectedRow = layout.tableJobClass.selectedRow.data as ModelRow;
                        this.activeRow.setLookupModelData("off_payee.job_class_code_id", selectedRow.getLookupModelData("id"));
                        this.activeRow.set("off_payee.job_class_code_id", selectedRow.get("id"));
                        this.activeRow.setLookupModelData("off_payee.division_id", selectedRow.getLookupModelData("division_id"));
                        this.activeRow.set("off_payee.division_id", selectedRow.get("division_id"));
                        this.activeRow.setLookupModelData("off_payee.terminal_id", selectedRow.getLookupModelData("terminal_id"));
                        this.activeRow.set("off_payee.terminal_id", selectedRow.get("terminal_id"));
                        this.buttonClearClassification.enabled = true;
                    }
                }
            }
        });
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxDrsPayeeearningCode. */
    textboxDrsPayeeearningCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.code_type = "E";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxDrsPayeedetentionTmEarningCode. */
    textboxDrsPayeedetentionTmEarningCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.code_type = "E";
    }

    /** This is an event handler for the onClick event of buttonClearClassification. */
    buttonClearClassificationOnClick(event: ClickEvent) {
        this.activeRow.setLookupModelData("off_payee.job_class_code_id", null);
        this.activeRow.set("off_payee.job_class_code_id", null);
        this.activeRow.setLookupModelData("off_payee.division_id", null);
        this.activeRow.set("off_payee.division_id", null);
        this.activeRow.setLookupModelData("off_payee.terminal_id", null);
        this.activeRow.set("off_payee.terminal_id", null);
        this.buttonClearClassification.enabled = false;
    }

    /** This is an event handler for the beforeExecution event of sourceLeaveVacation. */
    sourceLeaveVacationBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            event.filter.applies_to = "V";
        }
    }

    /** This is an event handler for the beforeExecution event of sourceLeaveSick. */
    sourceLeaveSickBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            event.filter.applies_to = "S";
        }
    }

    /** This is an event handler for the onClick event of buttonAdjustLeave. */
    buttonAdjustLeaveOnClick(event: ClickEvent) {
        LeaveHistory.showOfficeAsSlideout({
            leaveRow: this.activeRow,
            mode: DataSourceMode.ADD,
            onClose: (newLeave: ModelRow, balanceRow: ModelRow, postMade: boolean) => {
                if (!postMade) return; // If the leave transaction was not posted, do not refresh the leave sources
                this.sourceLeaveVacation.search(this.sourceLeaveVacation.lastSearch);
                this.sourceLeaveSick.search(this.sourceLeaveSick.lastSearch);
                const appliesTo = newLeave.get("applies_to");
                if (balanceRow) {
                    const field = appliesTo === "V" ? this.textboxOffPayeevacationHoursDue : this.textboxOffPayeesickHoursDue;
                    field.text = NumberUtil.formatDecimal(balanceRow.getNumber("amount"), "0.00");
                }

                if (newLeave.getBoolean("is_beginning_balance", false)) {
                    const formattedDate = DateUtil.formatDate(newLeave.getDate("trx_date"));
                    if (appliesTo === "V") {
                        this.textboxOffPayeevacLastAccrued.text = formattedDate;
                    } else {
                        this.textboxOffPayeesickLastAccrued.text = formattedDate;
                    }
                }
            }
        });
    }

    /** This is an event handler for the afterDropdownSelection event of textboxOffPayeewhenVacAccrues. */
    textboxOffPayeewhenVacAccruesAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            if (event.selection.value === ACCRUES_BEGIN_YEAR || event.selection.value === ACCRUES_ANNIVERSARY) {
                this.textboxOffPayeevacationHrsBase.enabled = false;
                this.textboxOffPayeevacationHrsBase.clear();
            } else {
                this.textboxOffPayeevacationHrsBase.enabled = true;
            }
        }
    }

    /** This is an event handler for the afterDropdownSelection event of textboxOffPayeewhenSickAccrues. */
    textboxOffPayeewhenSickAccruesAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            if (event.selection.value === ACCRUES_BEGIN_YEAR || event.selection.value === ACCRUES_ANNIVERSARY) {
                this.textboxOffPayeesickHoursBase.enabled = false;
                this.textboxOffPayeesickHoursBase.clear();
            } else {
                this.textboxOffPayeesickHoursBase.enabled = true;
            }
        }
    }


    /** This is an event handler for the beforeLookupModelSearch event of textboxDrsPayeetieredStopId. */
    textboxDrsPayeetieredStopIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    /** This is an event handler for the onChange event of checkboxOffPayeeenableCommissionsPay. */
    async checkboxOffPayeeenableCommissionsPayOnChange(event: ChangeEvent) {
        if (!event.newValue && this.activeRow.getBoolean("has_commissions_records", false)) {
            this.voidCommissions = await CommonDialogs.showYesNo("Commissions payments have been disabled, " +
                "and unpaid commissions records will no longer be created for this payee. " +
                "Do you want to void the existing unpaid commissions records for this payee?",
                "Unpaid Commissions");
        }

    }

    setPayeeButtonVisible(visible: boolean) {
        this.buttonSettlementPayee.visible = visible;
    }

    setupSettlementPayeeButton() {
        this.setPayeeButtonVisible(this.activeRow.getBoolean("non_office_emp", false) && !PermissionsUtil.isUserDeniedLayout("lme/settlement/DrsPayee"));
    }

    /** This is an event handler for the onClick event of buttonSettlementPayee. */
    buttonSettlementPayeeOnClick(event: ClickEvent) {
        const urlParams = { mode: DataSourceMode.UPDATE, key: this.activeRow.get("id") as string };
        const offPayeeUrl = "lme/settlement/DrsPayee" + UrlUtil.buildQueryString(urlParams);
        Navigation.navigateTo(offPayeeUrl, { newTab: true });
    }

    public static navigateTo(id: string): void {
        Navigation.navigateTo(viewOffPayeeUrl + id, { newTab: true });
    }

    textboxDepartmentOnChange(event: ChangeEvent) {
        this.textboxDepartment.text = this.textboxDepartment.text.substring(0, 3);
    }
}
